import Cookies from 'universal-cookie';
import { errorHelper } from '../helpers/errorHelper';

export interface HttpResponse<T> extends Response {
  parsedBody?: T;
  stringBody?: string;
}

const cookies = new Cookies();

async function http<T>(request: RequestInfo): Promise<HttpResponse<T>> {
  const response: HttpResponse<T> = await fetch(request);
  await response.text().then((body) => {
    try {
      response.parsedBody = JSON.parse(body);
    } catch (ex) {
      response.stringBody = body;
    }
  });

  if (!response.ok) {
    if (response.status === 401) {
      cookies.remove('token');
      window.location.pathname = '/login';
    }
    if (
      response.parsedBody !== undefined &&
      response.parsedBody !== null &&
      typeof response.parsedBody === 'object' &&
      'errorCode' in response.parsedBody
    ) {
      const code = (response.parsedBody as unknown as { errorCode: string })['errorCode'];
      const message = (response.parsedBody as unknown as { message: string })['message'];
      const userMessage =
        Number(code) < 1200 ? errorHelper.getErrorString(Number(code), message) : `Error ${code}: ${message}`;

  // Throw an Error object but attach extra properties so callers can inspect both
  // the original message (which may include JSON with QrCode) and a user-friendly message.
  const err: any = new Error(userMessage);
  err.errorCode = Number(code);
  err.originalMessage = message; // this may contain additional info
  err.userMessage = userMessage;
  // include full parsed body so callers can access nested payloads (like twoFactorSetupResponse)
  err.parsedBody = response.parsedBody;
  throw err;
    }

    throw new Error(response.statusText);
  }

  return response;
}

export async function get<T>(
  path: string,
  args: RequestInit = {},
  headers: Record<string, string> = {},
): Promise<HttpResponse<T>> {
  const defaultHeaders = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${new Cookies().get('token')}`,
  };

  const mergedHeaders = new Headers({ ...defaultHeaders, ...headers });

  const requestOptions: RequestInit = {
    method: 'get',
    headers: mergedHeaders,
    ...args,
  };

  return await http<T>(new Request(path, requestOptions));
}

export async function post<T>(
  path: string,
  body: any,
  args: RequestInit = {
    method: 'post',
    headers: new Headers({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${cookies.get('token')}`,
    }),
    body: JSON.stringify(body),
  },
): Promise<HttpResponse<T>> {
  return await http<T>(new Request(path, args));
}

export async function postForm<T>(
  path: string,
  body: FormData,
  args: RequestInit = {
    method: 'post',
    headers: new Headers({
      Authorization: `Bearer ${cookies.get('token')}`,
    }),
    body,
  },
): Promise<HttpResponse<T>> {
  return await http<T>(new Request(path, args));
}

export async function put<T>(
  path: string,
  body: any,
  args: RequestInit = {
    method: 'put',
    headers: new Headers({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${cookies.get('token')}`,
    }),
    body: JSON.stringify(body),
  },
): Promise<HttpResponse<T>> {
  return await http<T>(new Request(path, args));
}

export async function putForm<T>(
  path: string,
  body: FormData,
  args: RequestInit = {
    method: 'put',
    headers: new Headers({
      Authorization: `Bearer ${cookies.get('token')}`,
    }),
    body,
  },
): Promise<HttpResponse<T>> {
  return await http<T>(new Request(path, args));
}

export async function httpDelete<T>(
  path: string,
  body?: any,
  args: RequestInit = {
    method: 'delete',
    headers: new Headers({
      Authorization: `Bearer ${cookies.get('token')}`,
      'Content-Type': 'application/json',
    }),
    body: body ? JSON.stringify(body) : undefined,
  },
): Promise<HttpResponse<T>> {
  return await http<T>(new Request(path, args));
}
